import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import type {TextDocumentResponse} from "@mytour/shared";
import dayjs from "dayjs";
import {useState} from "react";
import {toast} from "react-toastify";
import {Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner} from "reactstrap";
import {autoLinkText} from "../autoLinkText";
import {useUpdateTextDocument} from "../query/documentQueries";
import {DocumentDeleteAction} from "./actions/DocumentDeleteAction";

type Props = {
  doc: TextDocumentResponse;
  open: boolean;
  onToggle: () => void;
};

/**
 * Lightbox for a note: read mode by default with Modifier / Supprimer actions; switching
 * to edit mode swaps the body to a textarea with Annuler / Enregistrer. Mirrors the file
 * lightbox UX so all card types share the same interaction pattern.
 */
export function NoteLightbox({doc, open, onToggle}: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(doc.body);
  const {updateTextDocument, isUpdatingTextDocument} = useUpdateTextDocument();

  const authorName = doc.member ? `${doc.member.firstName} ${doc.member.lastName}` : "Inconnu";
  const date = dayjs(doc.createdDate).format("D MMM YYYY · HH:mm");

  return (
    <Modal isOpen={open} toggle={handleToggle} size="lg" centered>
      <ModalHeader toggle={handleToggle}>Note</ModalHeader>
      <ModalBody>
        {editing ? (
          <textarea
            className="form-control"
            rows={6}
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={isUpdatingTextDocument}
          />
        ) : (
          <div style={{whiteSpace: "pre-wrap"}}>{autoLinkText(doc.body)}</div>
        )}
      </ModalBody>
      <ModalFooter className="d-flex justify-content-between align-items-center">
        <div className="text-muted small">
          {authorName} · {date}
        </div>
        <div className="d-flex gap-2">
          {editing ? (
            <>
              <Button color="secondary" onClick={handleCancel} disabled={isUpdatingTextDocument}>
                Annuler
              </Button>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={isUpdatingTextDocument || draft.trim().length === 0}
              >
                {isUpdatingTextDocument ? <Spinner size="sm" /> : <FontAwesomeIcon icon="check" />}
                &nbsp;Enregistrer
              </Button>
            </>
          ) : (
            <>
              <Button color="primary" onClick={handleEditClick}>
                <FontAwesomeIcon icon="pen" />
                &nbsp;Modifier
              </Button>
              <DocumentDeleteAction doc={doc} label="Supprimer" />
            </>
          )}
        </div>
      </ModalFooter>
    </Modal>
  );

  function handleToggle() {
    if (isUpdatingTextDocument) {
      return;
    }
    setEditing(false);
    setDraft(doc.body);
    onToggle();
  }

  function handleEditClick() {
    setDraft(doc.body);
    setEditing(true);
  }

  function handleCancel() {
    setEditing(false);
    setDraft(doc.body);
  }

  async function handleSave() {
    const body = draft.trim();
    if (body.length === 0) {
      return;
    }
    try {
      await updateTextDocument({documentId: doc.id, payload: {body}});
      setEditing(false);
      toast.success("Note modifiée", {autoClose: 2200});
    } catch {
      toast.error("Erreur lors de la modification de la note", {autoClose: 5000});
    }
  }
}
